import { createSelector } from '@reduxjs/toolkit';
import { ChatMember, Conversation } from '../../services/nebulaChat';
import { nebulaChatsAdapter, NebulaChatState } from './slice';
import { parseNumber, selectUsersDictionary } from '../authSlice';

export const nebulaChatEntitySelectors = nebulaChatsAdapter.getSelectors();

export const selectNebulaChats = (state: { chatN: NebulaChatState }): Conversation[] => nebulaChatEntitySelectors.selectAll(state.chatN.chats);

export const selectConversationById = (state: { chatN: NebulaChatState }, uuid?: string): Conversation | undefined => uuid ? nebulaChatEntitySelectors.selectById(state.chatN.chats, uuid) : undefined;

export const selectActiveConversation = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.chats,
    (state: { chatN: NebulaChatState }) => state.chatN.active_conversation,
], (chats, activeUuid): Conversation | undefined => {
    if (!activeUuid) {
        return undefined;
    }
    return nebulaChatEntitySelectors.selectById(chats, activeUuid);
})

export const selectChatMembersOnChat = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.chat_members,
    (_, chat_uuid?: string) => chat_uuid
], (members, chat_uuid) => chat_uuid ? (members[chat_uuid] || []).filter(m => !m.hidden) : undefined)

export const selectMemberOnChat = createSelector([
    selectChatMembersOnChat,
    (_, __, user_uuid: string) => user_uuid
], (members, user_uuid) => members?.find(m => m.member === user_uuid));

export const selectExtraChatters = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.extra_chatters,
], (chatters) => chatters);

export const selectExtraChatterByUuid = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.extra_chatters,
    (_, userUuid?: string) => userUuid
], (chatters, userUuid) => userUuid ? chatters[userUuid] : undefined);

export const selectConversationTypers = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.typing_users,
    (_, conversation: string) => conversation
], (typingUsers, conversation): string[] | undefined => typingUsers[conversation]);

export interface ChatMemberFull extends ChatMember {
    is_admin: boolean,
    is_creator: boolean,
    name: string,
}

export const selectSortedMembersOnChat = createSelector([
    selectUsersDictionary,
    selectExtraChatters,
    selectConversationById,
    selectChatMembersOnChat,
], (accountUsers, extraChatters, conversation, members) => {
    if (!members) {
        return []
    }

    const adminList: ChatMemberFull[] = members.map(m => ({
        ...m,
        is_admin: accountUsers[m.member]?.chat_config?.can_admin_all_public_groups || false,
        is_creator: conversation?.created_by === m.member,
        name: accountUsers[m.member]?.nickname || extraChatters[m.member]?.name || "Unknown User",
    }));

    adminList.sort((a, b) => {
        if (a.is_creator || (a.is_admin && !b.is_admin)) return -1;
        if (b.is_creator || (!a.is_admin && b.is_admin)) return 1;
        return a.name.localeCompare(b.name);
    })

    return adminList;
})

export const selectSortedActiveMembersOnChat = createSelector([
    selectUsersDictionary,
    selectExtraChatters,
    selectConversationById,
    selectChatMembersOnChat,
], (accountUsers, extraChatters, conversation, members) => {
    if (!members) {
        return []
    }

    const adminList: ChatMemberFull[] = [];

    members.forEach(m => {
        const accountUser = accountUsers[m.member];
        const extraChatter = extraChatters[m.member];

        if ((accountUser || extraChatter?.is_active) && !m.hidden) {
            adminList.push({
                ...m,
                is_admin: accountUser?.chat_config?.can_admin_all_public_groups || false,
                is_creator: conversation?.created_by === m.member,
                name: accountUser?.nickname || extraChatter?.name || "Unknown User",
            })
        }
    });

    adminList.sort((a, b) => {
        if (a.is_creator || (a.is_admin && !b.is_admin)) return -1;
        if (b.is_creator || (!a.is_admin && b.is_admin)) return 1;
        return a.name.localeCompare(b.name);
    })

    return adminList;
})

export const selectDestinationConversationUuid = createSelector([
    (state: { chatN: NebulaChatState }) => state.chatN.destination_conversations,
    (_state: any, phone_number?: string) => phone_number
], (mapper, phone_number): string | undefined => {
    if (!phone_number) {
        return undefined;
    }
    return mapper[parseNumber(phone_number)]
});

const selectConversationThreadsUnread = createSelector([
    (state: { chatN: NebulaChatState }) => nebulaChatEntitySelectors.selectAll(state.chatN.chats)
], (conversations): Conversation[] => conversations.filter(c => c.type === 'thread' && c.unread > 0))

export const selectConversationThreadUnreadSum = createSelector([
    (state: { chatN: NebulaChatState }) => nebulaChatEntitySelectors.selectEntities(state.chatN.chats),
    selectConversationThreadsUnread
], (conversations, unreadThreads) => {
    let sum = 0;

    unreadThreads.forEach(t => {
        const parent_uuid = t.parent_message?.to;

        if (parent_uuid && !t.is_muted) {
            const parent = conversations[parent_uuid];

            if (parent && !parent.is_muted) {
                sum += t.unread;
            }
        }
    })

    return sum;
})


export const selectUnreadConversationSum = createSelector([
    selectNebulaChats,
    selectConversationThreadUnreadSum
], (chats, threadUnread): number => {
    let sum = 0;

    chats.forEach(c => {
        if (!c.is_muted && c.type !== 'thread') {
            sum += c.unread;
        }
    })

    return sum + threadUnread;
});

export const selectConversationOtherMemberTimes = createSelector([
    selectSortedActiveMembersOnChat,
    (_, __, userUuid: string) => userUuid,
], (members, userUuid): { minReadAt: string | null; minDeliveredAt: string | null } => {
    if (!members || members.length <= 1) {
        return { minReadAt: '9999', minDeliveredAt: '9999' };
    }

    let minReadAt: string | null = null;
    let minDeliveredAt: string | null = null;
    let allRead = true;
    let allDelivered = true;

    const toISO = (ts: string) => ts.replace(' ', 'T').replace(/ ([+-])/, '$1');

    members.forEach(m => {
        if (m.member !== userUuid) {
            if (!m.last_read_at) {
                allRead = false;
            } else {
                const readAt = toISO(m.last_read_at);
                if (minReadAt === null || readAt.localeCompare(minReadAt) < 0) {
                    minReadAt = readAt;
                }
            }

            if (!m.last_delivered_at) {
                allDelivered = false;
            } else {
                const deliveredAt = toISO(m.last_delivered_at);
                if (minDeliveredAt === null || deliveredAt.localeCompare(minDeliveredAt) < 0) {
                    minDeliveredAt = deliveredAt;
                }
            }
        }
    });

    return {
        minReadAt: allRead ? minReadAt : null,
        minDeliveredAt: allDelivered ? minDeliveredAt : null,
    };
});
